import React, { useCallback } from 'react';
import cogoToast from 'cogo-toast';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import { useLazySearchSuperBatchModuleQuery } from 'services/superBatchModuleService';
import Select from 'components/Select';
import useDebouncedPromise from 'hooks/useDebouncedPromise';
import { menteeModuleTypeSelector } from 'features/moduleManagement';

const MIN_KEYWORD_LENGTH = 3;

export default function SuperBatchModuleAsyncSelect({
  superBatch,
  ...remainingProps
}) {
  const category = useSelector(menteeModuleTypeSelector);
  const [fetchSuperBatchModules, { isFetching }] =
    useLazySearchSuperBatchModuleQuery();

  const generateOptions = useCallback(
    (superBatchModules) =>
      superBatchModules?.map((o) => ({
        value: o.id,
        label: o.name,
        super_batch_module: superBatch.id,
      })),
    [superBatch]
  );

  const loadOptions = useDebouncedPromise(
    async (keyword, callback) => {
      if (keyword.length < MIN_KEYWORD_LENGTH) return callback([]);
      const response = await fetchSuperBatchModules(
        { category, keyword },
        true
      );
      let options = [];
      if (response.data) {
        options = generateOptions(response.data.data);
      } else {
        cogoToast.error(
          response.error?.data?.message ||
            'Failed to load super batch modules! Try again after sometime'
        );
      }
      callback(options);
    },
    DEBOUNCE_DURATION,
    [fetchSuperBatchModules, generateOptions, superBatch, category]
  );

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (inputValue.length < MIN_KEYWORD_LENGTH) {
        return `Type atleast ${MIN_KEYWORD_LENGTH} characters to search`;
      } else if (isFetching) {
        return `Searching...`;
      } else {
        return `No matching batch module found.`;
      }
    },
    [isFetching]
  );

  return (
    <Select
      type="async"
      placeholder="Select Batch Module"
      loadOptions={loadOptions}
      noOptionsMessage={noOptionsMessage}
      isDisabled={category === null ? true : false}
      {...remainingProps}
    />
  );
}

SuperBatchModuleAsyncSelect.propTypes = {
  superBatch: PropTypes.number,
};
